import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Activity, Smartphone, AlertTriangle, CheckCircle, WifiOff, MapPin, Clock } from 'lucide-react';
import { useAdminApi } from '../hooks/useAdminApi';

const HEALTH_META = {
  excellent: {
    label: 'Excellent',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/15',
    border: 'border-emerald-500/40',
    dot: 'bg-emerald-400',
    icon: CheckCircle,
    hint: 'Position < 3 min',
  },
  good: {
    label: 'Bon',
    color: 'text-lime-400',
    bg: 'bg-lime-500/15',
    border: 'border-lime-500/40',
    dot: 'bg-lime-400',
    icon: CheckCircle,
    hint: 'Position < 10 min',
  },
  warning: {
    label: 'Suspect',
    color: 'text-amber-400',
    bg: 'bg-amber-500/15',
    border: 'border-amber-500/40',
    dot: 'bg-amber-400',
    icon: AlertTriangle,
    hint: '10 - 60 min sans MAJ',
  },
  critical: {
    label: 'Critique',
    color: 'text-red-400',
    bg: 'bg-red-500/15',
    border: 'border-red-500/40',
    dot: 'bg-red-400',
    icon: AlertTriangle,
    hint: 'Aucune position depuis 1h+',
  },
  silent: {
    label: 'Silencieux',
    color: 'text-slate-400',
    bg: 'bg-slate-500/15',
    border: 'border-slate-500/40',
    dot: 'bg-slate-500',
    icon: WifiOff,
    hint: 'Jamais envoye de position',
  },
};

const formatMinutes = (m) => {
  if (m == null) return '—';
  if (m < 1) return '< 1 min';
  if (m < 60) return `${Math.round(m)} min`;
  const h = Math.floor(m / 60);
  const rest = Math.round(m % 60);
  if (h < 24) return `${h}h ${rest}m`;
  const d = Math.floor(h / 24);
  return `${d}j`;
};

const DriverGpsHealthPage = () => {
  const { get } = useAdminApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get('/admin/driver-gps-health');
      setData(res?.data || res);
      setError(null);
    } catch (e) {
      setError(e?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30_000); // refresh 30s
    return () => clearInterval(interval);
  }, [fetchData]);

  const drivers = data?.drivers || [];
  const summary = data?.summary || {};

  const filtered = filter === 'all' ? drivers : drivers.filter(d => d.health === filter);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="driver-gps-health-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Activity className="text-amber-500" />
            Santé GPS des chauffeurs
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Détecte les chauffeurs dont l'app ne remonte pas correctement ses positions.
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 disabled:opacity-50"
          data-testid="gps-health-refresh"
        >
          <RefreshCw size={18} className={`text-slate-300 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {['excellent', 'good', 'warning', 'critical', 'silent'].map(k => {
          const m = HEALTH_META[k];
          const Icon = m.icon;
          const count = summary[k] || 0;
          const active = filter === k;
          return (
            <button
              key={k}
              onClick={() => setFilter(active ? 'all' : k)}
              className={`rounded-xl border p-3 text-left transition-all ${m.bg} ${m.border} ${active ? 'ring-2 ring-amber-500' : 'hover:brightness-125'}`}
              data-testid={`gps-summary-${k}`}
            >
              <div className="flex items-center justify-between mb-1">
                <Icon size={16} className={m.color} />
                <span className={`text-2xl font-bold ${m.color}`}>{count}</span>
              </div>
              <p className={`text-xs font-semibold ${m.color}`}>{m.label}</p>
              <p className="text-slate-500 text-[10px] mt-0.5">{m.hint}</p>
            </button>
          );
        })}
      </div>

      {filter !== 'all' && (
        <button
          onClick={() => setFilter('all')}
          className="mb-3 text-xs text-amber-400 hover:underline"
        >
          ← Voir tous les chauffeurs
        </button>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-4 mb-4 text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Drivers list */}
      {loading && !data ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-slate-900 rounded-xl p-4 animate-pulse h-20" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500 text-sm">
          Aucun chauffeur dans cette catégorie.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(d => {
            const m = HEALTH_META[d.health] || HEALTH_META.silent;
            const Icon = m.icon;
            return (
              <div
                key={d.driver_id}
                className={`bg-slate-900 border ${m.border} rounded-xl p-3 flex items-center gap-3`}
                data-testid={`gps-driver-row-${d.driver_id}`}
              >
                {/* Pulse dot */}
                <div className="relative shrink-0">
                  <span className={`w-3 h-3 rounded-full ${m.dot} block`} />
                  {(d.health === 'excellent' || d.health === 'good') && (
                    <span className={`absolute inset-0 w-3 h-3 rounded-full ${m.dot} animate-ping opacity-60`} />
                  )}
                </div>

                {/* Name + email */}
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-semibold truncate">{d.name || d.email}</p>
                  <p className="text-slate-500 text-xs truncate">{d.email}</p>
                </div>

                {/* Platform */}
                <div className="hidden sm:flex items-center gap-1.5 shrink-0 text-slate-400 text-xs w-20">
                  <Smartphone size={12} />
                  <span>{d.platform || 'Web'}</span>
                </div>

                {/* Last position age */}
                <div className="flex items-center gap-1.5 shrink-0 text-slate-300 text-xs w-24">
                  <Clock size={12} className={m.color} />
                  <span>{formatMinutes(d.minutes_since_last)}</span>
                </div>

                {/* 24h positions */}
                <div className="hidden md:flex items-center gap-1.5 shrink-0 text-slate-400 text-xs w-24">
                  <MapPin size={12} />
                  <span>{d.positions_24h} / 24h</span>
                </div>

                {/* Health badge */}
                <div className={`shrink-0 px-2 py-1 rounded-full text-[10px] font-bold uppercase ${m.bg} ${m.color}`}>
                  <Icon size={10} className="inline mr-1" />
                  {m.label}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Auto-refresh hint */}
      <p className="text-slate-600 text-xs text-center mt-6">
        Auto-refresh toutes les 30 secondes • Dernière MAJ : {data?.generated_at ? new Date(data.generated_at).toLocaleTimeString('fr-CA') : '—'}
      </p>
    </div>
  );
};

export default DriverGpsHealthPage;
